import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { MdNotificationsActive } from "react-icons/md";
import { FiSave, FiClock, FiHash } from 'react-icons/fi';
import generateCaptcha from '../../components/captcha';
import AutoNotNav from './AutoNotNav';

const AutoNotification = () => {
    const [loader, setLoader] = useState(false);
    const [config, setConfig] = useState({
        n_send_limit: 1,
        n_start_time: '',
        n_end_time: '',
        is_enabled: true
    });

    // Fetch config on mount
    const fetchConfig = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getAutoNotConfig();
            if (resp?.status) {
                const data = resp.data;
                setConfig({
                    n_send_limit: data?.n_send_limit || 1,
                    n_start_time: data?.n_start_time || '',
                    n_end_time: data?.n_end_time || '',
                    is_enabled: data?.is_enabled ?? true
                });
            } else {
                toast.error(resp?.message || 'Failed to fetch config');
            }
        } catch (error) {
            console.error('Error fetching config:', error);
            toast.error('Failed to fetch configuration');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    // Validate HH:mm format
    const isValidTime = (time) => {
        return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
    };

    // Convert HH:mm to minutes for comparison
    const timeToMinutes = (time) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!config.n_start_time || !config.n_end_time) {
            toast.error('Start time and End time are required');
            return;
        }

        if (!isValidTime(config.n_start_time) || !isValidTime(config.n_end_time)) {
            toast.error('Invalid time format. Use HH:mm');
            return;
        }

        const startMin = timeToMinutes(config.n_start_time);
        const endMin = timeToMinutes(config.n_end_time);

        if (startMin >= endMin) {
            toast.error('End time must be greater than Start time');
            return;
        }

        if (config.n_send_limit < 1) {
            toast.error('Send limit must be at least 1');
            return;
        }
        const check = generateCaptcha(`You are about auto notification . To confirm, please solve this math problem.`)
        if (!check) toast.error("Captcha answer is incorrect", { autoClose: 2000 });

        setLoader(true);
        try {
            const body = {
                n_send_limit: Number(config.n_send_limit),
                n_start_time: config.n_start_time,
                n_end_time: config.n_end_time,
                is_enabled: config.is_enabled
            };

            const resp = await Calling.updateAutoNotConfig(body);
            if (resp?.status) {
                toast.success(resp?.message || 'Configuration updated successfully!');
                fetchConfig();
            } else {
                toast.error(resp?.message || 'Failed to update config');
            }
        } catch (error) {
            console.error('Error updating config:', error);
            toast.error('Failed to update configuration');
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            {loader ? (
                <Loader />
            ) : (
                <div className='w-full min-h-screen bg-main-bg text-main-font rounded-2xl'>
                    <div className="p-4">
                        {/* Header */}
                        <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                                    <MdNotificationsActive className="text-main-blue text-xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-main-font">Auto Notification Manager</h1>
                                    <p className="text-main-subfont text-sm mt-0.5">Configure automatic notification settings</p>
                                </div>
                            </div>
                        </div>

                        {/* Main Content Card */}
                        <div className='bg-main-white w-full rounded-2xl border border-main-border shadow-sm overflow-hidden'>
                            <AutoNotNav selectState={'config'} />

                            {/* Settings Header */}
                            <div className="flex items-center gap-3 p-5 md:p-6 border-b border-main-border bg-main-th/50">
                                <div className="w-10 h-10 bg-main-th rounded-lg flex items-center justify-center border border-main-blue/20">
                                    <MdNotificationsActive size={20} className="text-main-blue" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-main-font">Auto Notification Settings</h2>
                                    <p className="text-sm text-main-subfont">Configure when and how often notifications are sent</p>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="p-5 md:p-6">
                                {/* Enable/Disable Toggle */}
                                <div className="bg-main-bg rounded-xl border border-main-border p-4 mb-5">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <label className="text-main-font font-semibold block">Notification Status</label>
                                            <p className="text-sm text-main-subfont mt-0.5">Enable or disable automatic notifications</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${config.is_enabled
                                                ? 'bg-main-th text-main-blue border border-main-blue/20'
                                                : 'bg-main-delete/10 text-main-delete border border-main-delete/20'
                                                }`}>
                                                {config.is_enabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setConfig(prev => ({ ...prev, is_enabled: !prev.is_enabled }))}
                                                className={`relative w-14 h-7 rounded-full transition-all ${config.is_enabled ? 'bg-main-blue' : 'bg-main-border'}`}
                                            >
                                                <div className={`absolute top-1 w-5 h-5 bg-main-white rounded-full shadow-md transition-all ${config.is_enabled ? 'left-8' : 'left-1'}`} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Send Limit */}
                                <div className="mb-5">
                                    <label className="text-main-font font-semibold block mb-2" htmlFor="n_send_limit">
                                        Send Limit <span className="text-main-delete">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont">
                                            <FiHash size={18} />
                                        </div>
                                        <input
                                            type="number"
                                            name="n_send_limit"
                                            id="n_send_limit"
                                            value={config.n_send_limit}
                                            onChange={handleChange}
                                            min="1"
                                            className="w-full pl-11 pr-4 py-3 border border-main-border rounded-xl focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue bg-main-white text-main-font placeholder-main-subfont transition-all"
                                            placeholder="Enter send limit"
                                            required
                                        />
                                    </div>
                                    <p className="text-xs text-main-subfont mt-2">Maximum notifications to send per day</p>
                                </div>

                                {/* Time Range */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                                    {/* Start Time */}
                                    <div>
                                        <label className="text-main-font font-semibold block mb-2" htmlFor="n_start_time">
                                            Start Time (IST) <span className="text-main-delete">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont">
                                                <FiClock size={18} />
                                            </div>
                                            <input
                                                type="time"
                                                name="n_start_time"
                                                id="n_start_time"
                                                value={config.n_start_time}
                                                onChange={handleChange}
                                                className="w-full pl-11 pr-4 py-3 border border-main-border rounded-xl focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue bg-main-white text-main-font transition-all"
                                                required
                                            />
                                        </div>
                                        <p className="text-xs text-main-subfont mt-2">Format: HH:mm (24-hour)</p>
                                    </div>

                                    {/* End Time */}
                                    <div>
                                        <label className="text-main-font font-semibold block mb-2" htmlFor="n_end_time">
                                            End Time (IST) <span className="text-main-delete">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont">
                                                <FiClock size={18} />
                                            </div>
                                            <input
                                                type="time"
                                                name="n_end_time"
                                                id="n_end_time"
                                                value={config.n_end_time}
                                                onChange={handleChange}
                                                className="w-full pl-11 pr-4 py-3 border border-main-border rounded-xl focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue bg-main-white text-main-font transition-all"
                                                required
                                            />
                                        </div>
                                        <p className="text-xs text-main-subfont mt-2">Format: HH:mm (24-hour)</p>
                                    </div>
                                </div>

                                {/* Info Box */}
                                <div className="bg-main-th border border-main-blue/20 rounded-xl p-4 mb-6">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 bg-main-blue/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <MdNotificationsActive className="text-main-blue" size={16} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-main-font mb-1">Configuration Summary</p>
                                            <p className="text-sm text-main-blue">
                                                Auto notifications will be sent between <strong>{config.n_start_time || '--:--'}</strong> and <strong>{config.n_end_time || '--:--'}</strong> IST,
                                                up to <strong>{config.n_send_limit}</strong> time(s) per day.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="flex justify-end pt-4 border-t border-main-border">
                                    <button
                                        type="submit"
                                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-main-white font-semibold bg-main-blue hover:bg-[#2db84d] transition-all duration-200 shadow-md shadow-main-blue/25"
                                    >
                                        <FiSave size={18} />
                                        Save Settings
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AutoNotification;